import { apiFetchJson, authenticatedJsonRequest } from "./api"

// Pending trip invites surfaced by the presence payload ({ friends, invites }).
// This module owns the shaping/decision logic — which invites the map pill
// shows, what an accept/decline POSTs — so the map controller stays thin.
// The server owns the real rules (expiry, join-radius); it answers errors like
// { ok: false, error: "You're 3.2 mi away — ..." } which fetchJson throws as
// the message the pill displays.

// Invites still actionable for the viewer: has both ids, not dismissed
// locally (a successful decline) this page load.
export function pendingInvites(invites = [], dismissedIds = null) {
  const dismissed = dismissedIds instanceof Set ? dismissedIds : new Set(dismissedIds || [])
  return Array.from(invites || [])
    .filter(inv => inv && inv.id != null && inv.trip_id != null &&
      !dismissed.has(inv.id) && !dismissed.has(Number(inv.id)) && !dismissed.has(String(inv.id)))
    .map(inv => ({
      id: inv.id,
      tripId: inv.trip_id,
      from: inviteFromName(inv)
    }))
}

// Display name for the inviter: the payload only carries their email, so use
// the local part like friendName does.
export function inviteFromName(invite) {
  return String(invite?.from || "").split("@")[0].trim() || "A friend"
}

// Pill view-model: hidden when nothing is pending; the busy invite carries the
// spinner state + the server's error (e.g. a join-radius rejection on accept).
export function tripInvitesPillState(invites = [], { dismissedIds = null, busyId = null, error = null } = {}) {
  const visible = pendingInvites(invites, dismissedIds)
  const busy = invite => busyId != null && String(busyId) === String(invite.id)
  return {
    hidden: visible.length === 0,
    invites: visible.map(inv => ({
      ...inv,
      label: `${inv.from} invited you to join their trip`,
      busy: busy(inv),
      error: busy(inv) ? error : null
    }))
  }
}

// PATCH /api/mobile/trip_invites/:id (the api_mobile scope's update route).
export function inviteRespondPath(baseUrl, id) {
  return `${String(baseUrl || "").replace(/\/+$/, "")}/${id}`
}

// Accepting passes the viewer's last fix when known so the server's
// join-radius gate can verify proximity; it falls back to presence otherwise.
export function inviteRespondBody({ status, lat = null, lon = null } = {}) {
  const body = { status }
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    body.lat = lat
    body.lon = lon
  }
  return body
}

// Shared CSRF + JSON pattern (same headers the tracker/presence fetches use).
// Resolves { ok, status } on success; throws Error(server message) on failure.
export async function respondToInvite({ baseUrl, id, status, lat = null, lon = null, fetchImpl = null } = {}) {
  const options = {
    method: "PATCH",
    body: JSON.stringify(inviteRespondBody({ status, lat, lon }))
  }
  if (fetchImpl) return fetchImpl(inviteRespondPath(baseUrl, id), authenticatedJsonRequest(options))
  return apiFetchJson(inviteRespondPath(baseUrl, id), options)
}
